import * as React from 'react';

import { useWindowDimensions } from '@/helpers/hooks';
import { getEndOfMonthDate, getFullMonthName } from '@/helpers/utils/common';
import NoSsr from '@mui/material/NoSsr';
import Popper from '@mui/material/Popper';
import {
  chartsAxisHighlightClasses,
  chartsTooltipClasses,
  LineChart,
  lineElementClasses,
  lineHighlightElementClasses,
  markElementClasses,
} from '@mui/x-charts';

import { IMonthsWithRunId } from '../Commissions';

interface LinesChartProps {
  uData: number[];
  xLabels: string[];
  pData: number[];
  isBiMonthly: boolean;
  listOfMonthsWithRunerId: IMonthsWithRunId[];
  yAxisMax: number;
  ySteps: any;
}

const tickLabelStyling = {
  fontFamily: 'var(--font-sweet-sans-pro)',
  fontSize: 12,
  fill: '#999999',
  fontWeight: 400,
};

const LinesChart: React.FC<LinesChartProps> = ({
  uData,
  xLabels,
  pData,
  isBiMonthly,
  listOfMonthsWithRunerId,
  yAxisMax,
  ySteps,
}) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null);
  const [tooltipCoords, setTooltipCoords] = React.useState<{ x: number; y: number } | null>(null);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const handlePointerMove = (event: React.PointerEvent) => {
    if (!chartRef.current) return;

    const svg = chartRef.current.querySelector('svg');
    if (!svg) return;

    const chartArea = svg.querySelector('g');
    if (!chartArea) return;

    const chartBounds = chartArea.getBoundingClientRect();
    const mouseX = event.clientX - chartBounds.left;

    const usableWidth = chartBounds.width;
    const segmentWidth = usableWidth / (xLabels.length - 1);
    const rawIndex = mouseX / segmentWidth;
    const clampedIndex = Math.max(0, Math.min(xLabels.length - 1, Math.round(rawIndex)));

    const x = chartBounds.left + clampedIndex * segmentWidth;
    const y = chartBounds.top - 20;

    setHoverIndex(clampedIndex);
    setTooltipCoords({ x, y });
  };

  const handleLeave = () => {
    setHoverIndex(null);
    setTooltipCoords(null);
  };

  return (
    <div className="v2-w-full v2-overflow-x-auto">
      <div
        ref={chartRef}
        onPointerMove={handlePointerMove}
        onPointerLeave={handleLeave}
        style={{ touchAction: 'pan-x' }}
      >
        <LineChart
          height={252}
          series={[
            {
              data: uData,
              showMark: true,
              type: 'line',
              color: '#154734',
              baseline: 'min',
              valueFormatter: (v, { dataIndex }) => `${xLabels[dataIndex]}: $${v}`,
            },
            {
              data: pData,
              showMark: false,
              type: 'line',
              color: '#F79009',
            },
          ]}
          xAxis={[
            {
              scaleType: 'point',
              data: xLabels,
              disableLine: true,
              disableTicks: true,
              tickLabelStyle: {
                ...tickLabelStyling,
                fontSize: width < 768 ? 11 : 12,
                transform: width < 768 ? 'rotate(-90deg) translate(-10px, -10px)' : 'translate(-10px, 0px)',
                transformOrigin: 'start center',
                textAnchor: width < 768 ? 'end' : 'inherit',
              },
              tickLabelPlacement: 'tick',
            },
          ]}
          yAxis={[
            {
              scaleType: 'linear',
              min: 0,
              max: yAxisMax,
              tickInterval: ySteps,
              disableLine: true,
              disableTicks: true,
              tickLabelStyle: {
                ...tickLabelStyling,
                marginRight: '8px',
                fontSize: width < 768 ? 11 : 12,
              },
              id: 'uData',
              valueFormatter: value => (value < 1000 ? `${value}` : `${value / 1000}k`),
            },
            {
              scaleType: 'linear',
              min: 0,
              max: yAxisMax,
              disableLine: true,
              disableTicks: true,
              tickLabelStyle: tickLabelStyling,
              id: 'pData',
              valueFormatter: value =>
                value >= 1000
                  ? `${(value / 1000).toFixed(1)}k`
                  : value >= 100
                  ? `${Math.round(value)}`
                  : value.toFixed(2),
            },
          ]}
          grid={{ vertical: false, horizontal: true }}
          sx={{
            [`& .${lineElementClasses.root}`]: { strokeWidth: 3 },
            [`& .${lineHighlightElementClasses.root}`]: {
              r: 6,
              stroke: '#fff',
              fill: '#154734',
              strokeWidth: 4,
              filter: 'drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.2))',
              transition: 'all 0.2s ease-in-out',
            },
            [`& .${chartsAxisHighlightClasses.root}`]: {
              stroke: '#E5E5E5',
            },
            [`& .${chartsTooltipClasses.root}`]: {
              backgroundColor: 'transparent',
              pointerEvents: 'none',
            },
            [`& .${markElementClasses.root}`]: {
              fill: 'transparent',
              stroke: 'transparent',
            },
          }}
          tooltip={{
            trigger: isMobile ? 'none' : 'axis',
            axisContent: ({ axis, dataIndex, x, y }: any) => {
              const label = xLabels[dataIndex];
              const value = uData[dataIndex];
              const secondValue = pData[dataIndex];

              return (
                <div
                  style={{
                    position: 'absolute',
                    transform: 'translate(-50%, -140%)',
                    left: x,
                    top: y,
                    backgroundColor: '#333333',
                    backdropFilter: 'blur(40px)',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}
                >
                  {isBiMonthly ? (
                    <div>
                      <p className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-white v2-text-xs v2-mb-1">
                        {getFullMonthName(label)}
                      </p>
                      <div className="v2-flex v2-gap-1 v2-items-center">
                        <div className="v2-w-3 v2-h-[6px] v2-border-[0.5px] v2-bg-[#154734]" />
                        <span className="v2-font-sweet-sans-pro v2-font-normal v2-text-white v2-text-xs">
                          1st - 15th (RunID: {listOfMonthsWithRunerId[dataIndex].firstHalfRunId}): ${value}
                        </span>
                      </div>
                      <div className="v2-flex v2-gap-1 v2-items-center">
                        <div className="v2-w-3 v2-h-[6px] v2-border-[0.5px] v2-bg-[#F79009]" />
                        <span className="v2-font-sweet-sans-pro v2-font-normal v2-text-white v2-text-xs">
                          16th - {getEndOfMonthDate(label)}th (RunID:{' '}
                          {listOfMonthsWithRunerId[dataIndex].secondHalfRunId}
                          ): ${secondValue}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-white v2-text-xs">
                        {getFullMonthName(label)}:{' '}
                      </span>
                      <span className="v2-font-sweet-sans-pro v2-font-normal v2-text-white v2-text-xs">${value}</span>
                    </>
                  )}
                </div>
              );
            },
          }}
        />
      </div>

      {isMobile && hoverIndex !== null && tooltipCoords && (
        <NoSsr>
          <Popper
            open
            placement="top"
            anchorEl={{
              getBoundingClientRect: () => ({
                top: tooltipCoords.y,
                left: tooltipCoords.x,
                bottom: tooltipCoords.y,
                right: tooltipCoords.x,
                width: 0,
                height: 0,
                x: tooltipCoords.x,
                y: tooltipCoords.y,
                toJSON: () => {},
              }),
            }}
            modifiers={[{ name: 'preventOverflow', options: { boundary: 'viewport' } }]}
            sx={{ pointerEvents: 'none', zIndex: theme => theme.zIndex.tooltip }}
          >
            <div className="v2-bg-[#333333] v2-p-2 v2-rounded v2-text-white v2-text-xs v2-font-sweet-sans-pro">
              {isBiMonthly ? (
                <>
                  <div className="v2-font-bold v2-mb-1">{getFullMonthName(xLabels[hoverIndex])}</div>
                  <div className="v2-flex v2-gap-1 v2-items-center">
                    <div className="v2-w-3 v2-h-[6px] v2-bg-[#154734]" />
                    <span>
                      1st - 15th (RunID: {listOfMonthsWithRunerId[hoverIndex]?.firstHalfRunId}): ${uData[hoverIndex]}
                    </span>
                  </div>
                  <div className="v2-flex v2-gap-1 v2-items-center">
                    <div className="v2-w-3 v2-h-[6px] v2-bg-[#F79009]" />
                    <span>
                      16th - {getEndOfMonthDate(xLabels[hoverIndex])}th (RunID:{' '}
                      {listOfMonthsWithRunerId[hoverIndex]?.secondHalfRunId}): ${pData[hoverIndex]}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <strong>{getFullMonthName(xLabels[hoverIndex])}:</strong> ${uData[hoverIndex]}
                </>
              )}
            </div>
          </Popper>
        </NoSsr>
      )}
    </div>
  );
};

export default LinesChart;
